import type { MiraMode } from "@/data/models/MiraMode";
import {
  ClientContractData,
  ClientProductsValue,
  ClientTargetVersionField,
  MasterDataField,
  NotLiveReason,
} from "@/data/models/ClientAdministrationRow";
import { PartnerCreateRequest } from "@/data/models/ClientPartner";

/**
 * The guided setup of a new client in the IRES Admin Client (Track #2610082): what IRES Office sends to
 * `createClientForAdministration` and what `getClientSetupOptionsForAdministration` serves. IRES Cloud is
 * the authority (`services/clients/ClientCreationFields.ts`); see `docs/admin/client-setup.md`.
 */

/** How the new client stands in a group. */
export type ClientSetupGroupKind = "STANDALONE" | "MEMBER" | "MASTER";

export const CLIENT_SETUP_GROUP_KINDS: ClientSetupGroupKind[] = ["STANDALONE", "MEMBER", "MASTER"];

/**
 * An app a Projektorganisation question can name: `ProjectSettingsOptions`' values without the deprecated
 * BOTH / NEVER, as a string type so this model stays free of `Settings.ts`'s imports (Vuetify, Firestore).
 */
export type ProjectOrganisationApp = "PROJECT_MANAGER" | "DEVICE_MANAGER" | "PORTAL" | "API";

/** The four Projektorganisation questions, each a set of apps. */
export interface ClientSetupProjectOrganisation {
  newProjectsIn: ProjectOrganisationApp[];
  editProjectsIn: ProjectOrganisationApp[];
  finaliseProjectsIn: ProjectOrganisationApp[];
  newAppointmentsIn: ProjectOrganisationApp[];
}

/** The Funktionen step: the client's Systemkonfiguration switches the setup asks. */
export interface ClientSetupFunctions {
  enableTimeTracking: boolean;
  /** KARTE (the stored key keeps its old name). */
  enableGeoEncoding: boolean;
  /** TRACKING: requires KARTE. */
  enableTracking: boolean;
  enableAllDevicesManagement: boolean;
  /** The Cockpit tile of Alle Geräte: requires Alle Geräte. */
  enableDeviceManagementTile: boolean;
  enableVehicleManagement: boolean;
  enableScheduling: boolean;
  enablePersonalChat: boolean;
  enableProjectChat: boolean;
  /** ASSIST. */
  enableAiPredictions: boolean;
  /** `RenoActivationScope`'s values, as a string type so this model stays free of `Client.ts`'s imports. */
  renoActivationScope: "ALL_DRYINGS" | "ACTIVE_AND_NEW_ONLY";
  /** Who sees Mira (#2610141). */
  miraMode: MiraMode;
}

/** The user validation levels the first user can be given. */
export type ClientSetupValidationLevel = "LOW" | "NORMAL" | "STRICT";

export const CLIENT_SETUP_VALIDATION_LEVELS: ClientSetupValidationLevel[] = ["LOW", "NORMAL", "STRICT"];

/** The optional first user of the client, with the Administrator role. */
export interface ClientSetupFirstUser {
  firstName: string;
  lastName: string;
  nickname: string;
  staffNumber: string;
  emailAddress: string;
  temporaryPassword: string;
  validationLevel: ClientSetupValidationLevel;
}

/** A licence the setup counts, by the V3 record's key: B, A and T. */
export type ClientSetupLicenceKey = "basic" | "damageAssessmentAndLeakDetection" | "dryingAndRepair";

export const CLIENT_SETUP_LICENCE_KEYS: ClientSetupLicenceKey[] = ["basic", "damageAssessmentAndLeakDetection", "dryingAndRepair"];

/** The bought counts. */
export type ClientSetupLicences = Record<ClientSetupLicenceKey, number>;

/** How the client's Panels take a new firmware or configuration over IoT (IRES Admin's `IotUpdateCondition`). */
export type IotUpdateCondition = "ALWAYS" | "NEVER" | "WHEN_IDLE_OR_AFTER_POWER_ON";

export const IOT_UPDATE_CONDITIONS: IotUpdateCondition[] = ["ALWAYS", "NEVER", "WHEN_IDLE_OR_AFTER_POWER_ON"];

/** The two IoT update conditions the setup asks, as IRES Admin asks them; the ComBox timings are not asked. */
export interface ClientSetupDeviceUpdates {
  updateFirmwareViaIotCondition: IotUpdateCondition;
  updateConfigViaIotCondition: IotUpdateCondition;
}

/** The request for `createClientForAdministration`. A member sends no settings: it follows its master. */
export interface ClientSetupRequest {
  masterData: Record<MasterDataField, string | null>;
  email: string;
  password: string;
  notLiveReason: NotLiveReason | null;
  group: { kind: ClientSetupGroupKind; masterClientId: string | null };
  products: ClientProductsValue;
  projectTypeIds: number[] | null;
  projectOrganisation: ClientSetupProjectOrganisation | null;
  functions: ClientSetupFunctions | null;
  /** Every client, a member too; 0 for a licence the products do not need. */
  licences: ClientSetupLicences;
  /** Null for a member, which takes its master's. */
  contracts: ClientContractData | null;
  /** Null for a member, which takes its master's. */
  deviceUpdates: ClientSetupDeviceUpdates | null;
  firstUser: ClientSetupFirstUser | null;
  /** With API, for a client that is no member: the one external system created with it (#2610084, merge 4). */
  externalSystem: PartnerCreateRequest | null;
  /** With API, for a member: the master's external systems it is linked to. */
  linkedExternalSystemIds: string[];
}

/** What ANLEGEN returns: the new client, and the new external system with its password, shown once. */
export interface ClientSetupResult {
  clientId: string;
  externalSystem: { id: string; password: string } | null;
}

/** A MASTER-Mandant a new client can join; its settings only when asked for by id. */
export interface ClientSetupMaster {
  id: string;
  name: string;
  /** The group's Systemkonfiguration as stored, null when none is. */
  functions: Partial<ClientSetupFunctions> | null;
  /** The group's Projektorganisation as stored, null when none is. */
  projectOrganisation: Partial<ClientSetupProjectOrganisation> | null;
  projectTypeIds: number[];
  /** The Verträge a member takes over; null unless asked for by id (or from an IRES Cloud before merge 2). */
  contracts?: ClientContractData | null;
  /** The IoT update conditions a member takes over, each null where none is stored; null unless asked for. */
  deviceUpdates?: Record<keyof ClientSetupDeviceUpdates, IotUpdateCondition | null> | null;
  /** The master's external systems a member can be linked to (merge 4 of #2610084); null unless asked for. */
  externalSystems?: Array<{ id: string; name: string }> | null;
}

/** What the setup offers. */
export interface ClientSetupOptions {
  /** The possible project types for each switch combination, keyed "1010" (office, project, work, interface). */
  possibleProjectTypeIds: Record<string, number[]>;
  masters: ClientSetupMaster[];
  /** The fleet's target versions a new client that is no member follows; empty from an IRES Cloud before merge 2. */
  fleetTargets: Partial<Record<ClientTargetVersionField, string | null>>;
}

/** Why IRES Cloud refused a setup (`details.reason`). */
export type ClientSetupRefusal =
  | "INVALID"
  | "NOT_A_MASTER"
  | "PROJECT_TYPES_EMPTY"
  | "TRACKING_WITHOUT_MAP"
  | "TILE_WITHOUT_ALL_DEVICES"
  | "NICKNAME_TAKEN"
  | "STAFF_NUMBER_TAKEN"
  | "PREFIX_TAKEN"
  | "EMAIL_TAKEN"
  | "USER_EMAIL_TAKEN"
  | "IRES_ADMIN_EMAIL_TAKEN"
  | "SHARED_LOGIN"
  | "EXTERNAL_SYSTEM_EMAIL_TAKEN"
  | "NOT_THE_MASTERS_SYSTEM";

/** Every refusal the setup names, for matching IRES Cloud's `details.reason`. */
export const CLIENT_SETUP_REFUSALS: ClientSetupRefusal[] = [
  "INVALID",
  "NOT_A_MASTER",
  "PROJECT_TYPES_EMPTY",
  "TRACKING_WITHOUT_MAP",
  "TILE_WITHOUT_ALL_DEVICES",
  "NICKNAME_TAKEN",
  "STAFF_NUMBER_TAKEN",
  "PREFIX_TAKEN",
  "EMAIL_TAKEN",
  "USER_EMAIL_TAKEN",
  "IRES_ADMIN_EMAIL_TAKEN",
  "SHARED_LOGIN",
  "EXTERNAL_SYSTEM_EMAIL_TAKEN",
  "NOT_THE_MASTERS_SYSTEM",
];
